import classNames from "classnames";
import { Route as RouteIcon } from "lucide-react";

import { useAnalytics, useNavigation } from "@/providers";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import {
  RobotoButton,
  type RobotoButtonSize,
} from "@/shared/foundation/primitives/RobotoButton";

import styles from "./CreateSessionButton.module.css";

interface CreateSessionButtonProps {
  /**
   * When set, prefills the create-session page's file selection from this dataset.
   * Omit on unscoped surfaces (e.g. the top-level `/sessions` page).
   */
  datasetId?: string;
  /**
   * `sm` matches the Filter and Columns triggers when the button sits in the
   * `<SessionsList>` toolbar; `md` matches the other lists' empty-state
   * calls to action.
   * @default "sm"
   */
  size?: RobotoButtonSize;
}

/**
 * Entry point that opens the create-session flow. Rendered in the `actions`
 * slot of `<SessionsList>` on both the unscoped `/sessions` page and the
 * dataset-scoped Sessions tab, and again inside those lists' empty states.
 */
export function CreateSessionButton({
  datasetId,
  size = "sm",
}: CreateSessionButtonProps) {
  const { goto } = useNavigation();
  const { trackEvent } = useAnalytics();

  return (
    <RobotoTooltip title="Create a new session">
      <span>
        <RobotoButton
          variant="secondary"
          size={size}
          icon={<RouteIcon aria-hidden="true" />}
          className={classNames({ [styles.toolbarButton]: size === "sm" })}
          onClick={() => {
            trackEvent("CreateSessionClicked", { datasetId });
            goto.createSession({ datasetId });
          }}
        >
          New session
        </RobotoButton>
      </span>
    </RobotoTooltip>
  );
}
